import { useEffect, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { ListSingularDaysResponseSchema } from '@ebalance/proto/bffuser/v1/singular_day_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  HStack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaPlusRegular,
  FaTriangleExclamationSolid,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import type { SingularDayLabel } from '~/components/common/DayGrid'
import { HiddenFieldLabel } from '~/components/common/HiddenFieldLabel'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { iconSize } from '~/constants/iconSize'
import type { CivilDate } from '~/models/civilDate'
import { formatDateValue } from '~/models/dateTime'
import type { SingularDayRow } from '~/pages/AdminSingularDay/hooks/useListSingularDays'
import {
  convertListSingularDaysResponse,
  dateKeyOf,
  useListSingularDays,
} from '~/pages/AdminSingularDay/hooks/useListSingularDays'
import {
  matchesSingularDayAreaFilter,
  singularDayCategoryLabelKey,
} from '~/pages/AdminSingularDay/models/options'
import { isTargetDateRowLimitReached } from '~/pages/WorkflowCases/models/targetDateRowLimit'

type Props = {
  /** 選択済みの対象日 (SoT は呼び出し側の RHF `targetDates: string[]`。 ISO ⇔ `Date`
   *  変換は呼び出し側が component 境界で行う、 設計書 §6 Layer 間接続点)。 */
  value: Date[]
  /** 行の値が変わるたびに次の Date[] を通知する (重複日付は 1 件に集約する)。 */
  onChange: (nextDates: Date[]) => void
  /**
   * 作成対象のエリア (ダイアログの multi-select の現在値)。 特異日の注記を
   * **このエリアに関係するものだけ**に絞るために使う。
   * 特異日はエリア限定で登録でき (`areaCodes` 空 = 全エリア共通、 1 件以上 =
   * 特定エリア)、 絞らないと別エリア限定の特異日まで注記に出てしまう。
   * 未選択 (空配列) の間は全エリア共通のものだけ出す。
   */
  areaCodes?: AreaCode[]
}

type Row = { id: string; date: Date | undefined }

/** `CompactCivilDateField`（`CivilDate`）と本 component の境界型（`Date`）の変換。
 *  `~/components/common/CompactCivilDateField.tsx` 内の同名 private helper と同じ
 *  役割 (= 用途が閉じた 2 行の純関数なので、共有 module へ昇格せずこの境界にも
 *  複製する)。 */
function civilDateToJsDate(d: CivilDate): Date {
  return new Date(d.year, d.month - 1, d.day)
}
function jsDateToCivilDate(d: Date): CivilDate {
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
}

/**
 * 対象日入力 (design `demand/cases.html:535-539` [UX 2026-07-27] 準拠):
 * 1 行 = 1 日付の compact date picker 行を縦に並べ、「日付を追加」で行を増やす。
 * 行の削除ができ、行が 1 つだけの時は削除ボタンを隠す (design `_updateDateRemoveButtons`)。
 *
 * [設計変更 2026-08-13] 旧実装は `react-day-picker` ベースの
 * multi-select + Shift+range popover カレンダー (`TargetDateCalendar`、
 * design doc 4016-workflow-target-date-multi-select) だったが、8080 が
 * 2026-07-27 にその chip 形式を廃止し本行方式へ移行済みのため、design feedback
 * (2026-08-13) の指示でこちらへ揃える。**Shift+range（起点〜終点の一括選択）は
 * 8080 に存在しないため落とした** (行を 1 つずつ追加する操作に置き換え)。
 * デザイナー指摘「そもそも DS の date picker ではない」は本変更で解消する
 * (行の日付入力は DS `Calendar` primitive ベースの `CompactCivilDateField` を使う)。
 *
 * 総上限 `MAX_TARGET_DATES`（proto `target_dates.max_items = 31`）は 8080 に
 * 対応する UI が無いため、旧実装のヒント文 + 上限到達 Alert をそのまま踏襲する。
 *
 * 初期表示は行 1 つ・値未入力 (8080 の実装は当日の日付をプリフィルするが、
 * dev は spec workflow.md L385 / AC-01「初期状態は chips 空、業務ユーザーが
 * 明示的に選択して初めて登録する」が SoT のため、プリフィルはしない
 * [DS/spec 差異] — intake.md の SoT 優先順位: 承認済み spec > design prototype)。
 */
export const TargetDateFields = ({
  value,
  onChange,
  areaCodes = [],
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const idCounter = useRef(0)
  const makeRowId = (): string => {
    idCounter.current += 1
    return `target-date-row-${idCounter.current}`
  }

  const [rows, setRows] = useState<Row[]>(() =>
    value.length > 0
      ? value.map((d) => ({ id: makeRowId(), date: d }))
      : [{ id: makeRowId(), date: undefined }],
  )

  // 外部からの一括リセット (キャンセル / 送信成功後の `reset()`、
  // CreateWorkflowCasesDialog.tsx handleClose) を検知して行を空 1 行へ戻す。
  // `reset()` は RHF `targetDates` を `[]` に戻すが、本 component の行 UI 状態
  // (rows) はローカル state のため、呼び出し元 (ダイアログ) が unmount されない
  // 限り自動では追従しない。「rows 側に入力済みの日付が残っているのに value
  // (= RHF SoT) が空になった」ときだけ resync する (通常の 1 行ずつの追加/削除
  // 操作では value と rows の filled 件数は常に一致しているため誤発火しない)。
  const hasFilledRow = rows.some((r) => r.date !== undefined)
  useEffect(() => {
    if (value.length === 0 && hasFilledRow) {
      setRows([{ id: makeRowId(), date: undefined }])
    }
    // hasFilledRow を dep に含めると「行変更のたびに再評価」になり、
    // 通常の削除操作 (次段落) との競合順序に依存してしまうため、
    // 外部リセットの唯一のトリガーである value.length のみを見る。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.length])

  /** 行配列を確定し、重複日付を 1 件に集約した Date[] を親へ通知する
   *  (design `_effectiveDates()` の重複除去と同じ扱い。行自体は重複表示のまま残す)。 */
  const commit = (nextRows: Row[]): void => {
    setRows(nextRows)
    const seen = new Set<string>()
    const effective: Date[] = []
    for (const row of nextRows) {
      if (row.date === undefined) continue
      const key = formatDateValue(row.date)
      if (seen.has(key)) continue
      seen.add(key)
      effective.push(row.date)
    }
    onChange(effective)
  }

  const handleRowChange = (id: string, date: Date | undefined): void => {
    commit(rows.map((r) => (r.id === id ? { ...r, date } : r)))
  }

  const handleAddRow = (): void => {
    setRows((prev) => [...prev, { id: makeRowId(), date: undefined }])
  }

  const handleRemoveRow = (id: string): void => {
    const next = rows.filter((r) => r.id !== id)
    // 対象日は常に 1 行以上残す (design `_updateDateRemoveButtons`: 行が 1 つだけの
    // 時は削除ボタンを隠す = 全行削除させない)。
    commit(next.length > 0 ? next : [{ id: makeRowId(), date: undefined }])
  }

  /**
   * 上限判定 (proto `target_dates.max_items = 31`)。
   *
   * **行数**で見る。 送信される対象日は重複除去後の実効日数なので
   * `実効日数 <= 行数` が常に成り立ち、 行数を 31 以下に保てば超過は起きない。
   *
   * 実効日数で緩める案は破れる: 同じ日付を 31 行入れて実効 1 件にすれば行を
   * 増やせてしまい、 その後で各行を別の日付に**編集**すると 32 件以上が
   * フォームに入る (追加時だけを見ても、 値の変更経路が残る)。 レビューで
   * 2 度指摘された穴がこれ。
   *
   * 元の指摘の本質は「実効 1 件で上限 Alert も出ていないのに追加だけ無効」
   * という **判定と表示の不整合** だったので、 Alert も同じ行数基準に揃える。
   * これで「日付欄が 31 個あるので、 減らしてから追加する」と読める状態になる。
   */
  const isAddDisabled = isTargetDateRowLimitReached(rows.length)
  const isMaxReached = isTargetDateRowLimitReached(rows.length)

  /**
   * 特異日の注記 (#5638 Des 裁定 2026-08-18「A = DS 対応を待つ + B = 選択後に注記を
   * 出す」の B)。
   *
   * **行の注記とカレンダーの印の両方を出す。**
   *
   * 元はカレンダー内の色分けができなかった: 対象日入力は DS `Calendar` をそのまま
   * 使っており、 DS 0.14.2 の `CalendarProps` は value / maxDate / minDate /
   * includeDates / excludeDates / calendarType / onChange の 7 つしか無く、
   * 日付ごとの装飾フックが無い (DS 要望 eclair-designsystem#1222)。 その手当てとして
   * **選んだ日付の行に**「特異日: 名称（カテゴリ）」を出していた (#5638 の B 案)。
   *
   * [2026-09-16] `CompactCivilDateField` の中身を自前の `DayGrid` に置き換えた
   * (#8037) ことで、 DS 待ちだった**日付ごとの装飾が可能になった**。 A 案 (カレンダー
   * 上で分かる) を本来の形として足し、 行の注記は**残す** — 印は「この日が特異日だ」
   * までしか言えず、 名称とカテゴリは注記側にしか無いため。 spec AC-SC-027 /
   * AC-SC-028 (P0「特異日を見落として選ぶのを防ぐ」) はこれで選択前後の両方を覆う。
   *
   * 取得は年度を指定せず全年度 (対象日は年度をまたいで選べる)。 取得失敗・読み込み中は
   * 注記を出さないだけで、 日付選択自体は妨げない (graceful degrade)。
   */
  // 注記は画面の主役ではないので、 取得失敗で画面ごと落とさない
  // (global QueryClient の `throwOnError: true` をここだけ外す。
  //  `useListTradableAreas` と同じ fail-safe パターン)。
  const { data: singularDaysData } = useListSingularDays(undefined, {
    throwOnError: false,
  })

  /**
   * 日付 → その日に該当する特異日 (複数可)。
   *
   * ⚠ **日付は一意ではない。** 特異日マスタは同じ日に「全エリア共通」 と
   * 「特定エリア」、 あるいは異なる特定エリアの行を併存させられる
   * (`useListSingularDays` の doc)。 日付キーの Map に 1 件だけ持つと、
   * レスポンス順で最後の 1 件に上書きされ、 作成対象と無関係なエリアの名称を
   * 出したり他の特異日を見落としたりする (PR #5890 のレビュー指摘)。
   *
   * 絞り込みは一覧画面と同じ規則 (`matchesSingularDayAreaFilter`):
   * 全エリア共通 (areaCodes 空) か、 選択中のエリアを含む行。 該当が複数あれば
   * **全部出す** (どれを優先するかを勝手に決めない)。
   */
  const singularDaysByDateKey = new Map<string, SingularDayRow[]>()
  for (const day of convertListSingularDaysResponse(
    singularDaysData ?? create(ListSingularDaysResponseSchema, {}),
  )) {
    const relevant =
      areaCodes.length === 0
        ? day.areaCodes.length === 0
        : areaCodes.some((a) => matchesSingularDayAreaFilter(day.areaCodes, a))
    if (!relevant) continue
    const key = dateKeyOf(day.date)
    singularDaysByDateKey.set(key, [
      ...(singularDaysByDateKey.get(key) ?? []),
      day,
    ])
  }

  // カレンダー (日付入力の popup) にも同じ特異日を出す
  // (2026-09-16 デザイナー指摘「picker 上でも特異日を見たい / 今は見えない」)。
  // **行の注記と同じ絞り込み結果から作る**ので、注記に出る日とカレンダーで
  // 印が付く日が食い違うことはない。カテゴリは DayGrid が i18n を持たない
  // 契約 (`SingularDayLabel.categoryLabel` = 解決済みの表示名) なのでここで解決する。
  const singularDayLabelsByDateKey = new Map<string, SingularDayLabel[]>()
  for (const [key, days] of singularDaysByDateKey) {
    singularDayLabelsByDateKey.set(
      key,
      days.map((day) => ({
        name: day.name,
        categoryLabel: t(singularDayCategoryLabelKey(day.category)),
      })),
    )
  }

  return (
    <VStack align="stretch" gap={2}>
      <VStack align="stretch" gap={2}>
        {rows.map((row, idx) => {
          const fieldId = `${row.id}-input`
          const singularDays =
            row.date !== undefined
              ? (singularDaysByDateKey.get(dateKeyOf(row.date)) ?? [])
              : []
          return (
            <HStack key={row.id} gap={2}>
              {/* 日付入力の幅は **行によらず固定** (2026-08-19 デザイナー指摘
                  「日付入力部品は、 なぜ width が違う？ width はコンパクトに揃えたい」)。

                  design (`cases.html:1735`) は `flex:1` だが、 これは design の行が
                  「日付入力 + 削除ボタン」 だけで、 残余幅 = 常に同じだから成立して
                  いる。 本 app は同じ行に特異日の注記を置く (= 注記の有無と文字数で
                  残余幅が変わる) ため、 `flex:1` のままだと**行ごとに日付入力の幅が
                  変わる**。

                  固定値は共通の日付欄の幅 (`filters/DateStepper.tsx` の 1 行配置と
                  同じ `COMPACT_DATE_FIELD_WIDTH`)。 曜日付きの値 (`2026/08/20 (木)`) +
                  左の calendar icon 分の padding 34px が収まる幅。 */}
              <Box
                position="relative"
                flexShrink={0}
                width={COMPACT_DATE_FIELD_WIDTH}
              >
                {/* 各行の label text は同一文言だと getByLabelText が複数行を
                    区別できないため、既存の行番号付き i18n 慣習
                    (例: page:supplyBilateral.leg.remove = "leg #{{index}} を削除")
                    に倣い 1-based index を含める。視覚的には非表示 (input 内に
                    calendar icon のみで足りる、design 準拠)。 */}
                <HiddenFieldLabel htmlFor={fieldId}>
                  {t('page:workflowCases.form.targetDateRowLabel', {
                    index: idx + 1,
                  })}
                </HiddenFieldLabel>
                <CompactCivilDateField
                  id={fieldId}
                  onChange={(d) => {
                    handleRowChange(
                      row.id,
                      d !== undefined ? civilDateToJsDate(d) : undefined,
                    )
                  }}
                  singularDaysByDateKey={singularDayLabelsByDateKey}
                  value={
                    row.date !== undefined
                      ? jsDateToCivilDate(row.date)
                      : undefined
                  }
                />
              </Box>
              {/* 特異日の注記 (#5638)。 **日付入力と削除ボタンの間**に置く
                  (2026-08-19 デザイナー指摘)。

                  アイコンは `FaTriangleExclamationSolid` + `Object.warningMid`。
                  design プロトは独自 SVG の★を使っているが、 **DS のアイコンから選ぶ**
                  指示 (2026-08-19) に従い、 この repo で「入力欄まわりの注意メッセージ」
                  に既に使われている組み合わせへ寄せた
                  (`AdminOcctoMaster/.../TopLevelValidPeriodFields.tsx` の
                  `ValidToPastWarning` と同形)。 design 側の追随は別途起票する。

                  同じ日に複数該当する場合は全部並べる (どれを優先するかを勝手に
                  決めない。 PR #5890 のレビュー指摘)。

                  **注記が無い行では要素ごと出さない** (2026-09-17 デザイナー指摘
                  「X ボタンの表示位置を日付入力のすぐ右にしたい。 左に不要な余白が
                  ある」)。 以前は `flex="1"` で常に置いていたため、 注記が 0 件でも
                  残余幅を全部占有し、 削除ボタンが行の右端まで押し出されていた。
                  空の `HStack` を残すだけでも親の `gap` が 2 回分 (8px × 2) 入って
                  しまうので、 幅ではなく**要素ごと**条件付きにする。

                  注記がある行だけ `flex="0 1 auto"` (= 内容幅・縮小のみ) で
                  日付入力と削除ボタンの間に入る。 順序は 2026-08-19 の指摘
                  (「特異日の注記は日付入力と削除ボタンの間」) のまま維持する。 */}
              {singularDays.length > 0 && (
                <HStack
                  alignItems="center"
                  flex="0 1 auto"
                  gap={2}
                  minWidth={0}
                >
                  {singularDays.map((singularDay) => (
                    <HStack
                      key={singularDay.id}
                      alignItems="center"
                      gap={1}
                      minWidth={0}
                    >
                      <FaTriangleExclamationSolid
                        flexShrink={0}
                        boxSize={iconSize.fieldMessage}
                        color="Object.warningMid"
                      />
                      <Text textStyle="caption" color="Text.warningMid">
                        {t(
                          'page:demandForecastDaily.header.singularDayMarker.tooltip',
                          {
                            name: singularDay.name,
                            category: t(
                              singularDayCategoryLabelKey(singularDay.category),
                            ),
                          },
                        )}
                      </Text>
                    </HStack>
                  ))}
                </HStack>
              )}
              {/* 削除ボタンは **1 行のときも枠を残す**。
                  条件付きで枠ごと消すと行の構成が変わり、 左隣の特異日注記の位置が
                  1 行の時だけ右へずれる (2026-08-19 デザイナー指摘「X ボタンが
                  無い場合でも、 常に日付入力の右に表示したい」)。
                  design も同じ方式: `cases.html:1715` が
                  `rm.style.visibility = (rows.length <= 1) ? 'hidden' : 'visible'`
                  で**枠は残したまま見た目だけ消して**いる。

                  ボタン本体は 1 行のときは **DOM に出さない**。 `visibility: hidden`
                  だけだと `aria-label="対象日 1 を削除"` が残り、 Playwright の
                  `getByLabel('対象日 1')` (部分一致) が日付入力と削除ボタンの
                  2 要素に解決する (PR #5890 e2e / workflow-cases.spec.ts)。 */}
              <Box
                flexShrink={0}
                width="32px"
                height="32px"
                visibility={rows.length > 1 ? 'visible' : 'hidden'}
                aria-hidden={rows.length > 1 ? undefined : true}
              >
                {rows.length > 1 ? (
                  /* [DS差異] design (`cases.html` `.case-new-date-remove`) は
                      `btn btn-tertiary btn-square` (bordered) だが、本 app の
                      icon-only 行削除ボタンは既存パターン
                      (`SubmitterBgMemberFields.tsx` の member 行削除、
                      `CompactCivilDateField.tsx` の clear ボタン) に倣い DS
                      `IconButton variant="ghost"` を使う。 */
                  <IconOnlyButton
                    label={t('page:workflowCases.form.removeTargetDateRow', {
                      index: idx + 1,
                    })}
                    onClick={() => {
                      handleRemoveRow(row.id)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <FaXmarkRegular />
                  </IconOnlyButton>
                ) : null}
              </Box>
            </HStack>
          )
        })}
      </VStack>
      {/* 外側が VStack align="stretch" なので、 指定しないとボタンが横幅いっぱいに
          伸びて日付入力と同じ幅になる。 design は `demand/cases.html:539` の
          `style="align-self:flex-start;"` で内容幅に留めている
          (デザイナー指摘 2026-08-14「日付選択部品に合わせて横に伸ばさない」)。 */}
      {/* workflow.md AC-WFW-003: 可視文言とアクセシブル名をともに
          「日付を追加」へ揃え、タグの「追加」と区別する。 */}
      <TertiaryButton
        alignSelf="flex-start"
        aria-label={t('page:workflowCases.form.addTargetDate')}
        disabled={isAddDisabled}
        onClick={handleAddRow}
        size="sm"
      >
        <FaPlusRegular />
        {t('page:workflowCases.form.addTargetDate')}
      </TertiaryButton>
      {/* 上限に達して「日付を追加」 を非活性にした理由 (spec workflow.md §1
          機能詳細「対象日入力」)。 上限は proto `target_dates.max_items = 31` と整合。 */}
      {isMaxReached && (
        <Alert
          status="warning"
          title={t('page:workflowCases.form.targetDatesMaxReached')}
        />
      )}
    </VStack>
  )
}
